import { QueryKey, UseQueryOptions, useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useAtomValue } from 'jotai';

import { azarTokenAtom } from 'src/stores/auth/atoms';
import { client, clientWithoutAuth } from 'src/utils/api';

export type RestQueryOptions<TResponse = void, TError = void> = Omit<
  UseQueryOptions<AxiosResponse<TResponse>, AxiosError<TError>, AxiosResponse<TResponse>, QueryKey>,
  'queryKey' | 'queryFn'
>;

export const useRestQuery = <TParams, TResponse, TError>({
  path,
  params,
  withoutAuth = false,
  options,
  queryKey,
}: {
  path: string;
  params?: TParams;
  withoutAuth?: boolean;
  options?: RestQueryOptions<TResponse, TError>;
  queryKey?: QueryKey;
}) => {
  const token = useAtomValue(azarTokenAtom);
  const apiClient = withoutAuth ? clientWithoutAuth : client;

  return useQuery<AxiosResponse<TResponse>, AxiosError<TError>, AxiosResponse<TResponse>>({
    queryKey: queryKey ?? [path, params],
    queryFn: async () =>
      apiClient.get<TResponse>(path, {
        params: params ? { ...params } : null,
      }),
    meta: {
      ...options?.meta,
      withoutAuth,
    },
    enabled: withoutAuth ? true : !!token,
    ...options,
  });
};
